import { learningClient } from '../api'
import { IOffsetPaginationResponse } from '@sphere/shared/domain'
import {
  CreateActivityDto,
  UpdateActivityDto,
  ListActivitiesDto,
  SetStateDto,
  Activity,
  ActivityType,
  RemediationActivitiesResponse,
  StudentDifficultiesInsightResponse,
} from '@sphere/learning/domain'

export const activitiesService = {
  list: async (params: ListActivitiesDto) => {
    const response = await learningClient.activities.list(params)
    return response.data
  },
  getById: async (id: string) => {
    const response = await learningClient.activities.getById(id)
    return response.data
  },
  create: async (data: CreateActivityDto) => {
    const response = await learningClient.activities.create(data)
    return response.data
  },
  update: async (id: string, data: UpdateActivityDto) => {
    const response = await learningClient.activities.update(id, data)
    return response.data
  },
  delete: async (id: string) => {
    await learningClient.activities.delete(id)
  },
  clone: async (id: string) => {
    const response = await learningClient.activities.clone(id)
    return response.data
  },
  setState: async (id: string, data: SetStateDto) => {
    const response = await learningClient.activities.setState(id, data)
    return response.data
  },
  publish: async (id: string) => {
    const response = await learningClient.activities.publish(id)
    return response.data
  },
  setVisibility: async (id: string, visibility: string) => {
    const response = await learningClient.activities.setVisibility(
      id,
      visibility
    )
    return response.data
  },
  getTypes: async () => {
    const response = await learningClient.activities.getTypes()
    return response.data.data || []
  },
}

export async function fetchActivities(
  params: ListActivitiesDto
): Promise<IOffsetPaginationResponse<Activity>> {
  const response = await learningClient.activities.list(params)
  return {
    data: response.data.data || [],
    pageInfo: response.data.pageInfo || {
      page: 1,
      limit: 10,
      totalCount: 0,
      totalPages: 0,
      sort: 'createdAt',
      order: 'desc',
      hasNextPage: false,
      hasPreviousPage: false,
    },
  }
}

export async function fetchExercises(
  params: ListActivitiesDto
): Promise<IOffsetPaginationResponse<Activity>> {
  const response = await learningClient.activities.listExercises(params)
  return {
    data: response.data.data || [],
    pageInfo: response.data.pageInfo || {
      page: 1,
      limit: 10,
      totalCount: 0,
      totalPages: 0,
      sort: 'createdAt',
      order: 'desc',
      hasNextPage: false,
      hasPreviousPage: false,
    },
  }
}
export async function fetchRevisions(
  params: ListActivitiesDto
): Promise<IOffsetPaginationResponse<Activity>> {
  const response = await learningClient.activities.listRevisions(params)
  return {
    data: response.data.data || [],
    pageInfo: response.data.pageInfo || {
      page: 1,
      limit: 10,
      totalCount: 0,
      totalPages: 0,
      sort: 'createdAt',
      order: 'desc',
      hasNextPage: false,
      hasPreviousPage: false,
    },
  }
}

export async function fetchActivityById(id: string): Promise<Activity> {
  const response = await learningClient.activities.getById(id)
  return response.data
}

export async function fetchRemediationActivities(params?: {
  since_days?: number
  subject_id?: string
  weak_limit?: number
  activity_limit?: number
}): Promise<RemediationActivitiesResponse> {
  const response = await learningClient.activities.getRemediationActivities(params)
  return response.data
}

export async function fetchStudentDifficultiesInsight(
  userId: string,
  params?: {
    teaching_id?: string
    subject_id?: string
    since_days?: number
    weak_limit?: number
    activity_limit?: number
  }
): Promise<StudentDifficultiesInsightResponse> {
  const response =
    await learningClient.activities.getStudentDifficultiesInsight(userId, params)
  return response.data
}

export async function createActivity(
  data: CreateActivityDto
): Promise<Activity> {
  const response = await learningClient.activities.create(data)
  return response.data
}

export async function updateActivity(
  id: string,
  data: UpdateActivityDto
): Promise<Activity> {
  const response = await learningClient.activities.update(id, data)
  return response.data
}

export async function deleteActivity(id: string): Promise<void> {
  await learningClient.activities.delete(id)
}

export async function cloneActivity(id: string): Promise<Activity> {
  const response = await learningClient.activities.clone(id)
  return response.data
}

export async function setActivityState(
  id: string,
  data: SetStateDto
): Promise<Activity> {
  const response = await learningClient.activities.setState(id, data)
  return response.data
}

export async function publishActivity(
  id: string
): Promise<Activity> {
  const response = await learningClient.activities.publish(id)
  return response.data
}

export async function setActivityVisibility(
  id: string,
  visibility: string
): Promise<Activity> {
  const response = await learningClient.activities.setVisibility(id, visibility)
  return response.data
}

export async function fetchActivityTypes(): Promise<{
  data: ActivityType[]
  total: number
}> {
  const response = await learningClient.activities.getTypes()
  return {
    data: response.data.data || [],
    total: response.data.pageInfo.totalCount || 0,
  }
}

export async function fetchActivityType(type: string): Promise<ActivityType> {
  const response = await learningClient.activities.getType(type)
  return response.data
}

